import { memo, type FC, type ChangeEvent } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LightTooltip } from "@/components/LightTooltip";
import { FilterMethod } from "@/generated/company-search";

type MatchingOptionsProps = {
  /** Omit both to hide the toggle (e.g. Known Companies, which matches HG IDs exactly). */
  includeHierarchy?: boolean;
  onHierarchyChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Omit both to hide the toggle (e.g. Known Companies, where there is no country to match). */
  enforceCountryMatch?: boolean;
  onEnforceCountryMatchChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Prefix for the toggles' data-testids; defaults to "uploaded-accounts". */
  testIdPrefix?: string;
  filterMethod: FilterMethod;
  onFilterMethodChange: (event: ChangeEvent<HTMLInputElement>) => void;
  showAddFlag?: boolean;
  onAddFlagChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const MatchingOptions: FC<MatchingOptionsProps> = memo(
  ({
    includeHierarchy,
    onHierarchyChange,
    enforceCountryMatch,
    onEnforceCountryMatchChange,
    filterMethod,
    onFilterMethodChange,
    showAddFlag,
    onAddFlagChange,
    testIdPrefix = "uploaded-accounts",
  }) => {
    return (
      <Box
        sx={{
          mx: 2,
          mb: 2,
          p: 1.5,
          bgcolor: "grey.50",
          borderRadius: 1,
          border: 1,
          borderColor: "grey.200",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Matching Options
        </Typography>
        <Stack spacing={0.5} alignItems="flex-start">
          {onHierarchyChange && (
            <FormControlLabel
              control={
                <Switch
                  checked={!!includeHierarchy}
                  onChange={onHierarchyChange}
                  size="small"
                  data-testid={`${testIdPrefix}-hierarchy-toggle`}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption">
                    Include corporate hierarchy
                  </Typography>
                  <LightTooltip title="When enabled, matching a company will also include its parent and subsidiary companies">
                    <IconButton
                      size="small"
                      aria-label="Hierarchy matching help"
                      disableRipple
                      sx={{ p: 0.25 }}
                    >
                      <InfoOutlinedIcon
                        fontSize="inherit"
                        sx={{ fontSize: 16 }}
                      />
                    </IconButton>
                  </LightTooltip>
                </Stack>
              }
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
            />
          )}
          {onEnforceCountryMatchChange && (
            <FormControlLabel
              control={
                <Switch
                  checked={!!enforceCountryMatch}
                  onChange={onEnforceCountryMatchChange}
                  size="small"
                  data-testid={`${testIdPrefix}-country-match-toggle`}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption">
                    Only match same country
                  </Typography>
                  <LightTooltip title="When enabled, companies matched to a different country than the one provided in your file will be suppressed">
                    <IconButton
                      size="small"
                      aria-label="Enforce country match help"
                      disableRipple
                      sx={{ p: 0.25 }}
                    >
                      <InfoOutlinedIcon
                        fontSize="inherit"
                        sx={{ fontSize: 16 }}
                      />
                    </IconButton>
                  </LightTooltip>
                </Stack>
              }
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={filterMethod === FilterMethod.EXCLUDE_MATCHED}
                onChange={onFilterMethodChange}
                size="small"
                data-testid={`${testIdPrefix}-whitespace-toggle`}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption">
                  Only show companies not in this list (Whitespace)
                </Typography>
                <LightTooltip title="When enabled, only companies that do not match any row in your file will be shown">
                  <IconButton
                    size="small"
                    aria-label="Whitespace mode help"
                    disableRipple
                    sx={{ p: 0.25 }}
                  >
                    <InfoOutlinedIcon
                      fontSize="inherit"
                      sx={{ fontSize: 16 }}
                    />
                  </IconButton>
                </LightTooltip>
              </Stack>
            }
            sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
          />
          {showAddFlag && onAddFlagChange && (
            <FormControlLabel
              control={
                <Switch
                  checked={filterMethod === FilterMethod.ADD_FLAG}
                  onChange={onAddFlagChange}
                  size="small"
                  data-testid={`${testIdPrefix}-add-flag-toggle`}
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption">
                    Show everything, and mark matched companies in my final
                    export
                  </Typography>
                  <LightTooltip title="When enabled, the company list is not narrowed to your file; matched companies are flagged in the Universe export instead">
                    <IconButton
                      size="small"
                      aria-label="Mark matched companies help"
                      disableRipple
                      sx={{ p: 0.25 }}
                    >
                      <InfoOutlinedIcon
                        fontSize="inherit"
                        sx={{ fontSize: 16 }}
                      />
                    </IconButton>
                  </LightTooltip>
                </Stack>
              }
              sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
            />
          )}
        </Stack>
      </Box>
    );
  }
);

MatchingOptions.displayName = "MatchingOptions";
